import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiLinkVariant, mdiLinkVariantOff, mdiPlus, mdiPuzzleOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { useApi } from '../../api/hooks'
import { useAssetTypes } from '../../hooks/useQueries'
import {
  useAssetComponents,
  useDetachAssembly,
  useSetAssembly,
  componentErrorMessage,
  type AssetComponent,
} from '../../hooks/useComponents'
import { assetTypeDescendsFrom, type AssetTypeNode } from '../../lib/asset-type-inheritance'
import { mapEquipmentCondition } from '../../lib/equipment/conditionRollup'
import { CONDITION_STYLES } from './condition-utils'
import { AddEquipmentDialog } from './AddEquipmentDialog'
import { cn } from '../../lib/utils'

interface AssetTypeLite {
  uid: string
  code: string
  name?: Record<string, string> | null
}

function typeLabel(types: AssetTypeLite[], code: string, locale: string): string {
  const at = types.find((x) => x.code === code)
  const name = at?.name
  return (name && (name[locale] || name.en || name.fr)) || code
}

interface SiteEquipmentPickerProps {
  org: string
  siteUid?: string | null
  /** Excluded from the options (the asset itself). */
  excludeUids: string[]
  /** Types listed first (the usual assemblies of a component). */
  preferTypeCodes?: string[]
  onPick: (uid: string) => void
  testId: string
}

/**
 * Picks an equipment of the same site: the scope a component and its assembly
 * share (spec 2026-10-27-17).
 */
export function SiteEquipmentPicker({ org, siteUid, excludeUids, preferTypeCodes = [], onPick, testId }: SiteEquipmentPickerProps) {
  const { t } = useTranslation('assets')
  const { request } = useApi()
  const { data: assetTypes = [] } = useAssetTypes(org)
  const search = async (q: string): Promise<ApiSearchableSelectOption[]> => {
    try {
      const query: Record<string, string> = { limit: '50', sort: 'name' }
      if (q) query.q = q
      if (siteUid) query.ancestor_uid = siteUid
      const res = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
        params: { path: { orgCode: org }, query },
      })
      const rows = ((res as { data?: Array<{ uid: string; name?: string | null; asset_type_uid?: string | null }> }).data) ?? []
      const types = assetTypes as unknown as AssetTypeNode[]
      const codeOf = (uid?: string | null) => (assetTypes as Array<{ uid: string; code: string }>).find((x) => x.uid === uid)?.code ?? ''
      return rows
        .filter((a) => !excludeUids.includes(a.uid) && assetTypeDescendsFrom(types, a.asset_type_uid, 'equipment'))
        .sort((a, b) => {
          const pa = preferTypeCodes.includes(codeOf(a.asset_type_uid)) ? 0 : 1
          const pb = preferTypeCodes.includes(codeOf(b.asset_type_uid)) ? 0 : 1
          return pa - pb
        })
        .map((a) => ({ value: a.uid, label: a.name || a.uid }))
    } catch {
      return []
    }
  }
  return (
    <ApiSearchableSelect
      onValueChange={(v) => v && onPick(v)}
      onSearch={search}
      minSearchLength={0}
      defaultOpen
      placeholder={t('components.pick_placeholder')}
      searchPlaceholder={t('components.pick_placeholder')}
      emptyMessage={t('components.pick_empty')}
      data-testid={testId}
    />
  )
}

interface ComponentsCardProps {
  org: string
  assetUid: string
  assetName?: string
}

/**
 * The components of an equipment, and what it is part of (spec 2026-10-27-17).
 * Components are `part_of` edges, never tree children: each keeps its own
 * location, shown on its row.
 */
export function ComponentsCard({ org, assetUid, assetName }: ComponentsCardProps) {
  const { t, i18n } = useTranslation('assets')
  const { data: view } = useAssetComponents(org, assetUid)
  const { data: assetTypesRaw = [] } = useAssetTypes(org)
  const assetTypes = assetTypesRaw as unknown as AssetTypeLite[]
  const setAssembly = useSetAssembly(org)
  const detach = useDetachAssembly(org)
  const [mode, setMode] = useState<'idle' | 'link' | 'change'>('idle')
  const [addOpen, setAddOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const components = useMemo(() => view?.components ?? [], [view?.components])
  const suggested = view?.suggested_type_codes ?? []
  const assemblyCodes = view?.assembly_type_codes ?? []

  if (!view) return null
  const hasComponentsSection = components.length > 0 || suggested.length > 0
  const hasPartOfLine = !!view.assembly || assemblyCodes.length > 0
  if (!hasComponentsSection && !hasPartOfLine) return null

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      await fn()
      setMode('idle')
    } catch (err) {
      setError(componentErrorMessage(err, t))
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="components-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AppIcon tone="muted" path={mdiPuzzleOutline} size="md" />
          <h3 className="text-sm font-medium">{t('components.card_title')}</h3>
          {components.length > 0 && <Badge variant="outline">{components.length}</Badge>}
        </div>
        {hasComponentsSection && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="components-add">
              <AppIcon path={mdiPlus} size="sm" className="mr-1" />
              {t('components.add')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMode(mode === 'link' ? 'idle' : 'link')}
              data-testid="components-link"
            >
              <AppIcon path={mdiLinkVariant} size="sm" className="mr-1" />
              {t('components.link')}
            </Button>
          </div>
        )}
      </div>

      {hasPartOfLine && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="components-assembly">
          {view.assembly ? (
            <>
              <span className="text-muted-foreground">{t('components.part_of')}</span>
              <Link
                to="/$org/assets/$assetUid"
                params={{ org, assetUid: view.assembly.uid }}
                className="font-medium hover:underline"
                data-testid="components-assembly-link"
              >
                {view.assembly.name || typeLabel(assetTypes, view.assembly.type_code, i18n.language)}
              </Link>
              <Button size="sm" variant="ghost" onClick={() => setMode(mode === 'change' ? 'idle' : 'change')} data-testid="components-assembly-change">
                {t('components.change')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void run(() => detach.mutateAsync(assetUid))}
                data-testid="components-assembly-detach"
              >
                {t('components.detach')}
              </Button>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">{t('components.part_of_none')}</span>
              <Button size="sm" variant="ghost" onClick={() => setMode(mode === 'change' ? 'idle' : 'change')} data-testid="components-assembly-set">
                {t('components.attach')}
              </Button>
            </>
          )}
        </div>
      )}

      {mode !== 'idle' && (
        <div className="mt-3 max-w-md">
          <SiteEquipmentPicker
            org={org}
            siteUid={view.site_uid}
            excludeUids={[assetUid, ...components.map((c) => c.uid)]}
            preferTypeCodes={mode === 'change' ? assemblyCodes : suggested}
            testId={mode === 'link' ? 'components-link-picker' : 'components-assembly-picker'}
            onPick={(uid) =>
              void run(() => (mode === 'link' ? setAssembly.mutateAsync(uid, assetUid) : setAssembly.mutateAsync(assetUid, uid)))
            }
          />
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-destructive" role="alert" data-testid="components-error">
          {error}
        </p>
      )}

      {hasComponentsSection && (
        components.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="components-empty">
            {t('components.empty')}
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-border/60">
            {components.map((c) => (
              <ComponentRow
                key={c.uid}
                org={org}
                component={c}
                typeName={typeLabel(assetTypes, c.type_code, i18n.language)}
                onUnlink={() => void run(() => detach.mutateAsync(c.uid))}
              />
            ))}
          </ul>
        )
      )}

      <AddEquipmentDialog
        org={org}
        open={addOpen}
        onOpenChange={setAddOpen}
        parentAssetUid={assetUid}
        assemblyUid={assetUid}
        assemblyLabel={assetName}
        suggestedTypeCodes={suggested}
      />
    </div>
  )
}

function ComponentRow({
  org,
  component: c,
  typeName,
  onUnlink,
}: {
  org: string
  component: AssetComponent
  typeName: string
  onUnlink: () => void
}) {
  const { t, i18n } = useTranslation('assets')
  const condition = mapEquipmentCondition(c.condition)
  const style = CONDITION_STYLES[condition]
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm" data-testid={`components-row-${c.uid}`}>
      <div className="min-w-0 flex-1">
        <Link to="/$org/assets/$assetUid" params={{ org, assetUid: c.uid }} className="font-medium hover:underline">
          {c.name || typeName}
        </Link>
        <div className="truncate text-xs text-muted-foreground">
          {[typeName, c.location?.name].filter(Boolean).join(' · ')}
        </div>
      </div>
      <Badge className={cn(style?.pill)}>{t(`equipment_tab.condition.${condition}`)}</Badge>
      <span className="text-xs text-muted-foreground" title={t('components.last_intervention')}>
        {c.last_intervention_at
          ? new Date(c.last_intervention_at).toLocaleDateString(i18n.language)
          : t('components.never_serviced')}
      </span>
      {c.component_count > 0 && (
        <Link
          to="/$org/assets/$assetUid"
          params={{ org, assetUid: c.uid }}
          className="text-xs text-primary hover:underline"
          data-testid={`components-row-${c.uid}-count`}
        >
          {t('components.count', { count: c.component_count })}
        </Link>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onUnlink}
        aria-label={t('components.unlink')}
        title={t('components.unlink')}
        data-testid={`components-unlink-${c.uid}`}
      >
        <AppIcon path={mdiLinkVariantOff} size="sm" />
      </Button>
    </li>
  )
}

/**
 * Archiving or deleting an equipment keeps its components in place, detached
 * (spec 2026-10-27-17): the confirmation says so.
 */
export function ComponentsDetachedNotice({ org, assetUid, enabled }: { org: string; assetUid: string; enabled: boolean }) {
  const { t } = useTranslation('assets')
  const { data } = useAssetComponents(org, assetUid, { enabled })
  const count = data?.components?.length ?? 0
  if (!enabled || count === 0) return null
  return (
    <p className="text-sm text-muted-foreground" data-testid="components-detached-notice">
      {t('components.detached_notice', { count })}
    </p>
  )
}
